import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { negativeSaleMrpService } from '../services'

const DEFAULT_ROWS = 10

const formatMoney = (value) =>
  value != null && value !== '' && Number.isFinite(Number(value))
    ? Number(value).toFixed(2)
    : '—'

const extractApiError = (err, fallback) => {
  const data = err?.response?.data
  if (Array.isArray(data?.message)) return data.message.join(', ')
  if (typeof data?.message === 'string' && data.message.trim()) return data.message
  return fallback
}

export default function NegativeSaleMrpSection({ orgId }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS)
  const [total, setTotal] = useState(0)

  const [editOpen, setEditOpen] = useState(false)
  const [editRow, setEditRow] = useState(null)
  const [editMrp, setEditMrp] = useState('')
  const [editRemarks, setEditRemarks] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const [dialogError, setDialogError] = useState(null)
  const [tpConfirmOpen, setTpConfirmOpen] = useState(false)

  const [resettingId, setResettingId] = useState(null)
  const [toast, setToast] = useState(null)

  const fetchList = useCallback(async () => {
    if (!orgId) return
    try {
      setLoading(true)
      setError(null)
      const res = await negativeSaleMrpService.list(orgId, {
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
      })
      setItems(res?.items || [])
      setTotal(res?.pagination?.total || 0)
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load negative sale MRP list')
      setItems([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [orgId, page, rowsPerPage, search])

  useEffect(() => {
    setPage(0)
  }, [orgId])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const openEdit = (row) => {
    setEditRow(row)
    setEditMrp(row.overrideMrp != null ? String(row.overrideMrp) : '')
    setEditRemarks(row.remarks || '')
    setDialogError(null)
    setEditOpen(true)
  }

  const belowTpWarning = (() => {
    const mrp = Number(editMrp)
    const tp = Number(editRow?.systemTp)
    if (!Number.isFinite(mrp) || mrp <= 0) return null
    if (editRow?.systemTp == null || !Number.isFinite(tp) || mrp >= tp) return null
    return `Entered MRP is less than TP (BDT ${formatMoney(tp)}).`
  })()

  const persistMrp = async () => {
    if (!orgId || !editRow?.adminProductId) return
    const mrp = Number(editMrp)
    try {
      setEditSaving(true)
      setDialogError(null)
      const res = await negativeSaleMrpService.upsert(orgId, editRow.adminProductId, {
        mrp,
        remarks: editRemarks.trim() || undefined,
      })
      setToast({ severity: 'success', message: res?.message || 'MRP updated.' })
      setTpConfirmOpen(false)
      setEditOpen(false)
      setDialogError(null)
      fetchList()
    } catch (err) {
      setTpConfirmOpen(false)
      setEditOpen(true)
      setDialogError(extractApiError(err, 'Failed to save MRP override'))
    } finally {
      setEditSaving(false)
    }
  }

  const handleSave = () => {
    if (!orgId || !editRow?.adminProductId) return
    const mrp = Number(editMrp)
    if (!Number.isFinite(mrp) || mrp <= 0) {
      setDialogError('Enter a valid MRP.')
      return
    }
    if (belowTpWarning) {
      setEditOpen(false)
      setTpConfirmOpen(true)
      return
    }
    persistMrp()
  }

  const cancelBelowTp = () => {
    setTpConfirmOpen(false)
    setEditOpen(true)
  }

  const handleReset = async (row) => {
    if (!orgId || !row?.adminProductId || row.overrideMrp == null) return
    try {
      setResettingId(row.adminProductId)
      const res = await negativeSaleMrpService.remove(orgId, row.adminProductId)
      setToast({ severity: 'success', message: res?.message || 'Override removed.' })
      fetchList()
    } catch (err) {
      setToast({
        severity: 'error',
        message: err?.response?.data?.message || 'Failed to reset override',
      })
    } finally {
      setResettingId(null)
    }
  }

  if (!orgId) return null

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Negative sale MRP (catalog)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set org-specific MRP for articles in this grocer&apos;s purchasable catalog
          (mapped DC) with no on-hand stock at this shop, including articles never
          stocked here. Selling them is a negative sale (does not change master
          catalog or other shops).
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Search article"
            placeholder="Article code / product name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={() => {
              setPage(0)
              setSearch(searchInput.trim())
            }}
            disabled={loading}
            sx={{ minWidth: 90 }}
          >
            Apply
          </Button>
        </Stack>

        {toast && (
          <Alert
            severity={toast.severity}
            sx={{ mb: 2 }}
            onClose={() => setToast(null)}
          >
            {toast.message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Article</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">On-hand</TableCell>
                    <TableCell align="right">System MRP</TableCell>
                    <TableCell align="right">Override MRP</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        No eligible catalog articles found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow key={row.adminProductId}>
                        <TableCell>{row.articleCode || '—'}</TableCell>
                        <TableCell>{row.productName || '—'}</TableCell>
                        <TableCell align="right">{row.stockQty ?? 0}</TableCell>
                        <TableCell align="right">{formatMoney(row.systemMrp)}</TableCell>
                        <TableCell align="right">
                          {row.overrideMrp != null ? (
                            <Chip
                              size="small"
                              color="primary"
                              label={formatMoney(row.overrideMrp)}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => openEdit(row)}
                          >
                            Edit
                          </Button>
                          {row.overrideMrp != null && (
                            <Button
                              size="small"
                              color="warning"
                              startIcon={<RestartAltIcon />}
                              disabled={resettingId === row.adminProductId}
                              onClick={() => handleReset(row)}
                            >
                              Reset
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}

        <Dialog
          open={editOpen}
          onClose={() => {
            setEditOpen(false)
            setDialogError(null)
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editRow?.overrideMrp != null ? 'Edit' : 'Set'} negative sale MRP
          </DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}
            <Stack spacing={2} sx={{ mt: dialogError ? 0 : 1 }}>
              <Typography variant="body2">
                <strong>{editRow?.articleCode}</strong> — {editRow?.productName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                System MRP: {formatMoney(editRow?.systemMrp)}
                {editRow?.systemTp != null ? ` · TP: ${formatMoney(editRow.systemTp)}` : ''}
              </Typography>
              <TextField
                label="Override MRP"
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                value={editMrp}
                onChange={(e) => setEditMrp(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Remarks (optional)"
                value={editRemarks}
                onChange={(e) => setEditRemarks(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setEditOpen(false)
                setDialogError(null)
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={editSaving}>
              {editSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={tpConfirmOpen} onClose={cancelBelowTp} maxWidth="xs" fullWidth>
          <DialogTitle>MRP below TP</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {belowTpWarning}
            </Alert>
            <Typography>Are you sure?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelBelowTp} disabled={editSaving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={persistMrp} disabled={editSaving}>
              {editSaving ? 'Saving…' : 'OK'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  )
}
